/**
 * Outward embedder notifications.
 *
 * The SPA notifies the embedding page (iframe parent or popup/new-tab opener)
 * with sanitized events:
 * - `LD_TURBOVETS_READY` / `LD_TURBOVETS_PREFILL_ACK` — the bidirectional
 *   readiness handshake (WI-TV-W2-002).
 * - `LD_TURBOVETS_COMPLETE` / `LD_TURBOVETS_ERROR` — submission outcomes
 *   (WI-TV-W1-006).
 *
 * All messages are posted ONLY to runtime-configured, validated origins with an
 * explicit `targetOrigin` (never `"*"`), and never contain borrower PII, field
 * names/values, raw request/response bodies, the API key/auth header, or stack
 * traces. `environment` is emitted on COMPLETE/ERROR only when the runtime
 * debug flag is on; the READY handshake always includes the environment label
 * as non-secret protocol metadata.
 *
 * The inbound `TURBOVETS_PREFILL` parsing/mapping in `autofill.ts` is unchanged;
 * this module only reports its outcome via the acknowledgement.
 */
import type { ApiErrorKind } from "./api";
import type { CreateReferralResponse, ReferralLinks } from "./models/referral";
import { PREFILL_MESSAGE_TYPE, type PrefillOutcome } from "./autofill";

export const MESSAGE_COMPLETE = "LD_TURBOVETS_COMPLETE";
export const MESSAGE_ERROR = "LD_TURBOVETS_ERROR";
export const MESSAGE_READY = "LD_TURBOVETS_READY";
export const MESSAGE_PREFILL_ACK = "LD_TURBOVETS_PREFILL_ACK";

/** Handshake protocol version advertised in the READY message. */
export const PROTOCOL_VERSION = 1 as const;

/** Inbound message types this SPA accepts from TurboVets. */
export const MESSAGE_TYPES_ACCEPTED: readonly string[] = [PREFILL_MESSAGE_TYPE];

/** Outbound message types this SPA can emit to the embedder. */
export const MESSAGE_TYPES_EMITTED: readonly string[] = [
  MESSAGE_READY,
  MESSAGE_PREFILL_ACK,
  MESSAGE_COMPLETE,
  MESSAGE_ERROR,
];

/** Success event payload. Allowed fields only; `environment` is debug-only. */
export interface CompleteMessage {
  type: typeof MESSAGE_COMPLETE;
  messageId: string;
  correlationId: string;
  partnerReferenceId?: string;
  referralId: string;
  status: string;
  submittedAt: string;
  links?: ReferralLinks;
  environment?: string;
}

/** Error event payload. Sanitized fields only; `environment` is debug-only. */
export interface ErrorMessage {
  type: typeof MESSAGE_ERROR;
  messageId: string;
  correlationId: string;
  partnerReferenceId?: string;
  status?: number;
  errorKind: ApiErrorKind;
  retryable: boolean;
  message: string;
  environment?: string;
}

export type EmbedderMessage = CompleteMessage | ErrorMessage;

/**
 * Readiness handshake payload (WI-TV-W2-002). Emitted once after the inbound
 * autofill listener is installed so embedders know when it is safe to send
 * `TURBOVETS_PREFILL`. Contains only non-secret protocol metadata.
 */
export interface ReadyMessage {
  type: typeof MESSAGE_READY;
  messageId: string;
  protocolVersion: typeof PROTOCOL_VERSION;
  environment: string;
  messageTypesAccepted: readonly string[];
  messageTypesEmitted: readonly string[];
}

export type PrefillAckStatus = "applied" | "ignored" | "rejected";
export type PrefillAckReason = "no-known-fields" | "invalid-shape";

/**
 * Acknowledgement for a handled inbound `TURBOVETS_PREFILL` (WI-TV-W2-002).
 * Reports the outcome without echoing any borrower field names or values.
 */
export interface PrefillAckMessage {
  type: typeof MESSAGE_PREFILL_ACK;
  messageId: string;
  correlationId?: string;
  status: PrefillAckStatus;
  reason?: PrefillAckReason;
  appliedFieldCount: number;
}

/** Any message this SPA posts outward to an embedder. */
export type OutboundMessage = EmbedderMessage | ReadyMessage | PrefillAckMessage;

/** Browser-safe, non-guessable id identifying a single outward message. */
export function createMessageId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export interface CompleteMessageInput {
  correlationId: string;
  response: CreateReferralResponse;
  environment: string;
  debug: boolean;
  /** Falls back to the EPS response value when omitted. */
  partnerReferenceId?: string;
  messageId?: string;
}

/**
 * Builds a sanitized `LD_TURBOVETS_COMPLETE` payload from the EPS response.
 * Only contract-allowed, non-PII fields are copied across.
 */
export function buildCompleteMessage(input: CompleteMessageInput): CompleteMessage {
  const message: CompleteMessage = {
    type: MESSAGE_COMPLETE,
    messageId: input.messageId ?? createMessageId(),
    correlationId: input.correlationId,
    referralId: input.response.referralId,
    status: input.response.status,
    submittedAt: input.response.submittedAt,
  };

  const partnerReferenceId = input.response.partnerReferenceId ?? input.partnerReferenceId;
  if (partnerReferenceId) message.partnerReferenceId = partnerReferenceId;

  const links = input.response.links;
  if (links && (links.status || links.detail)) {
    const safeLinks: ReferralLinks = {};
    if (links.status) safeLinks.status = links.status;
    if (links.detail) safeLinks.detail = links.detail;
    message.links = safeLinks;
  }

  if (input.debug) message.environment = input.environment;

  return message;
}

export interface ErrorMessageInput {
  correlationId: string;
  errorKind: ApiErrorKind;
  /** Already-sanitized friendly message. Never a raw response or stack trace. */
  message: string;
  retryable: boolean;
  status?: number;
  partnerReferenceId?: string;
  environment: string;
  debug: boolean;
  messageId?: string;
}

/** Builds a sanitized `LD_TURBOVETS_ERROR` payload. */
export function buildErrorMessage(input: ErrorMessageInput): ErrorMessage {
  const message: ErrorMessage = {
    type: MESSAGE_ERROR,
    messageId: input.messageId ?? createMessageId(),
    correlationId: input.correlationId,
    errorKind: input.errorKind,
    retryable: input.retryable,
    message: input.message,
  };

  if (typeof input.status === "number") message.status = input.status;
  if (input.partnerReferenceId) message.partnerReferenceId = input.partnerReferenceId;
  if (input.debug) message.environment = input.environment;

  return message;
}

export interface ReadyMessageInput {
  environment: string;
  messageId?: string;
}

/**
 * Builds the `LD_TURBOVETS_READY` handshake payload. Carries only non-secret
 * protocol metadata: version, environment label, and the accepted/emitted
 * message-type lists. Never includes borrower PII, the API key, or payloads.
 */
export function buildReadyMessage(input: ReadyMessageInput): ReadyMessage {
  return {
    type: MESSAGE_READY,
    messageId: input.messageId ?? createMessageId(),
    protocolVersion: PROTOCOL_VERSION,
    environment: input.environment,
    messageTypesAccepted: [...MESSAGE_TYPES_ACCEPTED],
    messageTypesEmitted: [...MESSAGE_TYPES_EMITTED],
  };
}

export interface PrefillAckMessageInput {
  status: PrefillAckStatus;
  appliedFieldCount: number;
  reason?: PrefillAckReason;
  correlationId?: string;
  messageId?: string;
}

/** Builds a `LD_TURBOVETS_PREFILL_ACK` payload from explicit fields. */
export function buildPrefillAckMessage(input: PrefillAckMessageInput): PrefillAckMessage {
  const message: PrefillAckMessage = {
    type: MESSAGE_PREFILL_ACK,
    messageId: input.messageId ?? createMessageId(),
    status: input.status,
    appliedFieldCount: input.appliedFieldCount,
  };
  if (input.reason) message.reason = input.reason;
  if (input.correlationId !== undefined) message.correlationId = input.correlationId;
  return message;
}

/**
 * Maps an inbound prefill outcome to its acknowledgement message:
 * - `invalid-shape`   -> status "rejected", reason "invalid-shape"
 * - `no-known-fields` -> status "ignored",  reason "no-known-fields"
 * - otherwise (applied path) -> status "applied" with the applied field count.
 *
 * The `correlationId` (when the sender supplied one) is echoed back. No field
 * names or values are ever included. `disallowed-origin` outcomes must never be
 * passed here — the caller does not acknowledge disallowed origins.
 */
export function buildPrefillAck(outcome: PrefillOutcome, messageId?: string): PrefillAckMessage {
  if (outcome.reason === "invalid-shape") {
    return buildPrefillAckMessage({
      status: "rejected",
      reason: "invalid-shape",
      appliedFieldCount: 0,
      correlationId: outcome.correlationId,
      messageId,
    });
  }

  if (outcome.reason === "no-known-fields") {
    return buildPrefillAckMessage({
      status: "ignored",
      reason: "no-known-fields",
      appliedFieldCount: 0,
      correlationId: outcome.correlationId,
      messageId,
    });
  }

  return buildPrefillAckMessage({
    status: "applied",
    appliedFieldCount: outcome.fields.length,
    correlationId: outcome.correlationId,
    messageId,
  });
}

/**
 * Collects candidate embedder windows: the iframe parent and/or the popup
 * opener. The current window is never targeted.
 */
export function embedderTargets(win: Window): Window[] {
  const targets: Window[] = [];
  const parent = win.parent;
  if (parent && parent !== win) targets.push(parent);
  const opener = win.opener as Window | null;
  if (opener && opener !== win) targets.push(opener);
  return targets;
}

/**
 * Posts a message to each target window once per allowed origin, always with an
 * explicit `targetOrigin`. The browser only delivers a given post when the
 * target window's actual origin matches, so this fans out safely without ever
 * using a wildcard origin. Returns the number of `postMessage` calls made.
 */
export function postToTargets(
  targets: readonly (Window | null | undefined)[],
  message: OutboundMessage,
  allowedOrigins: readonly string[],
): number {
  if (allowedOrigins.length === 0) return 0;

  let count = 0;
  const seen = new Set<Window>();
  for (const target of targets) {
    if (!target || seen.has(target)) continue;
    seen.add(target);
    for (const origin of allowedOrigins) {
      try {
        target.postMessage(message, origin);
        count += 1;
      } catch {
        // Closed window or cross-origin access error: skip this target.
      }
    }
  }
  return count;
}

/**
 * Notifies the embedder of `win` (parent and/or opener) with `message`, posting
 * only to the validated `allowedOrigins`. No-op when there is no embedder or no
 * configured origins.
 */
export function notifyEmbedder(
  win: Window,
  message: OutboundMessage,
  allowedOrigins: readonly string[],
): number {
  return postToTargets(embedderTargets(win), message, allowedOrigins);
}
